import { useEffect, useState } from "react";

import { ExposureMeter } from "../components/ExposureMeter";
import { IntelOpsPanel } from "../components/IntelOpsPanel";
import { LiveSafetyBanner } from "../components/LiveSafetyBanner";
import { StrategyCard } from "../components/StrategyCard";
import { TraderJournalPanel } from "../components/TraderJournalPanel";
import { ApiError, api } from "../lib/api";
import { STRATEGIES, type StatusResponse, type StrategyKey } from "../lib/types";

type Props = {
  onOpenStrategy: (s: StrategyKey) => void;
  onAuthFail: () => void;
};

const POLL_MS = 30_000;

export function OverviewPage({ onOpenStrategy, onAuthFail }: Props) {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.status();
        if (!cancelled) {
          setStatus(s);
          setErr(null);
          setRefreshedAt(Date.now());
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          onAuthFail();
          return;
        }
        if (!cancelled) setErr(e instanceof Error ? e.message : "failed");
      }
    };
    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [onAuthFail]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-50">
          trader-bot
        </h1>
        <div className="flex items-baseline gap-3 text-xs text-slate-500">
          {refreshedAt && (
            <span>
              refreshed{" "}
              <span className="font-mono">
                {new Date(refreshedAt).toLocaleTimeString()}
              </span>
            </span>
          )}
          <button
            onClick={() => api.logout().finally(onAuthFail)}
            className="rounded border border-ink-700 px-2 py-1 text-slate-400 hover:text-slate-200"
          >
            sign out
          </button>
        </div>
      </header>

      {err && (
        <div className="rounded-md border border-accent-err bg-accent-err/10 px-3 py-2 text-sm text-accent-err">
          {err}
        </div>
      )}

      {/* Top of the page — the bot's live-trading safety state. Renders
          red when the kill switch is active, amber when real-money
          trading is armed, green otherwise. */}
      <LiveSafetyBanner cfg={status?.live_config ?? null} />

      <ExposureMeter exposure={status?.exposure ?? null} />

      <section
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
        data-testid="strategy-grid"
      >
        {STRATEGIES.map((s) => (
          <StrategyCard
            key={s}
            strategy={s}
            block={
              status?.strategies[s] ?? {
                heartbeat: null,
                nav_latest: null,
                decisions_today: { allowed: 0, denied: 0 },
              }
            }
            onOpen={onOpenStrategy}
          />
        ))}
      </section>

      {/* DuckDB analytical readouts — signal frequency, guardrail
          decision analytics, fleet reliability — from loop-driver's
          tailer-fed store. */}
      <IntelOpsPanel />

      {/* The head-trader agent's per-fire journal. Reads what the
          agent thinks about cross-strategy state, with flags surfaced
          prominently. Latest entry expanded by default. */}
      <TraderJournalPanel />
    </div>
  );
}
